// @flow strict
/**
 * @author brandonwilliams
 * @since June 2018
 */
import * as React from 'react';
import classNames from '@ta/common.classnames';

import styles from './Skeleton.css';

type CSSValue = string | number;

type Props = {|
  /** height of the skeleton, can be any valid value of the height css property. Defaults to undefined. */
  height?: CSSValue,

  /** Width of the skeleton, can be any valid value of the width css property. Defaults to undefined. */
  width?: CSSValue,

  /** whitespace above the skeleton, can be any valid value of the margin-top css property. Defaults to undefined. */
  marginTop?: CSSValue,

  /** white-space below the skeleton, can be any valid value of the margin-bottom css property. Defaults to undefined. */
  marginBottom?: CSSValue,

  /** content to render inside of the skeleton. Defaults to undefined. */
  children?: React.Node,

  /** when true, the skeleton will position absolutely and fill its entire relative area. Defaults to false. */
  fillParent?: boolean,
|};

/**
 *  Displays a skeleton with an animated background that acts as a placeholder for content that is still loading.
 *
 * @param height - height of the skeleton, can be any valid value of the height css property.
 * @param width - The width of the skeleton, can be any valid value of the width css property.
 * @param marginTop - whitespace above the skeleton, can be any valid value of the margin-top css property.
 * @param marginBottom - white-space below the skeleton, can be any valid value of the margin-bottom css property.
 * @param children - content to render inside of the skeleton
 * @param fillParent - when true, the skeleton will position absolutely and fill its entire relative area.
 */
const Skeleton = ({
  height,
  width,
  marginTop,
  marginBottom,
  children,
  fillParent = false,
}: Props) => (
  <div
    className={classNames(styles.skeleton, { [styles.fill]: fillParent })}
    style={{ height, width, marginTop, marginBottom }}
    // We rely on imperatively selecting this element for some of our legacy applications.
    // This field is deprecated and should not actively be selected against!
    data-skeleton="@ta/styleguide.skeleton"
  >
    {children}
  </div>
);

export default Skeleton;
